import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export interface TransactionDTO {
  id: number;
  date: string;
  name: string;
  description: string;
  confirmNotes: string;
  accountNumber: string;
  units: number;
  price: number;
  total: number;
  ticker: string;
  notes: string | null;
}

export interface GroupedTransactionsDTO {
  groupedTransactions: { name: string; total: number; transactions: TransactionDTO[] }[];
  beginningMarketValue: number;
  endingMarketValue: number;
  marketValueChange: number;
  accruedIncomeChange?: number;
  showTransactionDetail: boolean;
}

interface FetcherParams {
  clientId: string;
  accountId?: string;
  groupId?: string;
  period?: number;
  startDate?: string;
  endDate?: string;
}

const fetchGroupedTransactions = async ({
  clientId,
  accountId,
  groupId,
  period,
  startDate,
  endDate,
}: FetcherParams): Promise<GroupedTransactionsDTO> => {
  return authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/GroupedTransactions',
      {
        clientId,
        accountId: accountId || null,
        groupId: groupId || null,
        period,
        startDate,
        endDate,
      },
      { skipNulls: true },
    ),
  );
};

interface QueryParams {
  clientId: string;
  accountId?: string;
  groupId?: string;
  period?: number;
  startDate?: string;
  endDate?: string;
}

export const useGroupedTransactionsQuery = (
  { clientId, accountId, groupId, period, startDate: _startDate, endDate: _endDate }: QueryParams,
  { enabled }: { enabled: boolean } = { enabled: true },
) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useQuery(
    queryKeys.groupedTransactions({
      clientId,
      accountId,
      groupId,
      period,
      startDate,
      endDate,
    }),
    () =>
      fetchGroupedTransactions({
        clientId,
        accountId,
        groupId,
        period,
        startDate,
        endDate,
      }),
    {
      enabled,
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
    },
  );
};
